import { useTranslation } from 'react-i18next';
import { useMutation } from '@tanstack/react-query';
import { CREDIT_PACKAGES } from '@boletra/shared';
import { DashboardShell } from '../../components/DashboardShell.js';
import { useCurrentOrg, useOrgCreditPackages, useOrgCredits } from '../../lib/orgs.js';
import { api, ApiClientError } from '../../lib/api.js';
import { OnboardingPage } from './OnboardingPage.js';

const CREDITS_PENDING_KEY = 'boletra_credits_pending';

export function CreditsPage() {
  const { t, i18n } = useTranslation('dashboard');
  const fmtUsd = new Intl.NumberFormat(i18n.language, {
    style: 'currency',
    currency: 'USD',
    currencyDisplay: 'code',
    minimumFractionDigits: 2,
  });
  const fmtDate = new Intl.DateTimeFormat(i18n.language, {
    year: 'numeric',
    month: 'short',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
  const org = useCurrentOrg();
  const credits = useOrgCredits(org?.id ?? null);
  const packagesQuery = useOrgCreditPackages(org?.id ?? null);

  const checkout = useMutation({
    mutationFn: (packageId: string) =>
      api<{ url: string }>(`/orgs/${org?.id}/credits/checkout`, {
        method: 'POST',
        body: { packageId },
      }),
    onSuccess: ({ url }, packageId) => {
      const pkg = CREDIT_PACKAGES.find((p) => p.id === packageId);
      if (pkg) {
        sessionStorage.setItem(
          CREDITS_PENDING_KEY,
          JSON.stringify({ packageId, credits: pkg.credits, totalUsdCents: pkg.totalUsdCents }),
        );
      }
      window.location.href = url;
    },
  });

  if (!org) return <OnboardingPage />;

  // Mientras el endpoint localizado responde, mostramos los paquetes en USD
  // (fuente canónica de `@boletra/shared`) para evitar un layout shift.
  // Cuando llega la respuesta los reemplazamos con el shape localizado.
  const localized = packagesQuery.data;
  const displayCurrency = localized?.displayCurrency ?? 'USD';
  const isEstimate = localized?.isEstimate ?? false;
  // El precio en moneda local con código (MXN, JPY) puede pasar los 12
  // caracteres en planes grandes. Pedimos siempre `currencyDisplay:'code'`
  // para una salida predecible y partimos número + código con formatToParts
  // así renderizamos el número grande y la divisa más chica al costado, sin
  // empujar el layout cuando la cifra crece.
  const fmtLocal = new Intl.NumberFormat(i18n.language, {
    style: 'currency',
    currency: displayCurrency,
    currencyDisplay: 'code',
    minimumFractionDigits: 2,
  });
  const visiblePackages = localized
    ? localized.packages
    : CREDIT_PACKAGES.map((p) => ({
        id: p.id,
        credits: p.credits,
        totalCents: p.totalUsdCents,
        perUnitCents: p.perUnitCents,
        totalUsdCents: p.totalUsdCents,
        perUnitUsdCents: p.perUnitCents,
      }));

  const balance = credits.data?.balance ?? 0;
  const free = credits.data
    ? Math.max(0, credits.data.freeMonthlyAllowance - credits.data.freeMonthlyConsumed)
    : 0;

  return (
    <DashboardShell>
      <h1 className="text-3xl font-bold mb-1">{t('credits.title')}</h1>
      <p className="text-neutral-400 mb-8">{t('credits.subtitle')}</p>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4 mb-10">
        <div className="card">
          <p className="text-xs uppercase tracking-wider text-neutral-500">
            {t('credits.balance_label')}
          </p>
          <p className="text-3xl font-bold mt-2">{balance.toLocaleString()}</p>
        </div>
        <div className="card">
          <p className="text-xs uppercase tracking-wider text-neutral-500">
            {t('credits.free_this_month_label')}
          </p>
          <p className="text-3xl font-bold mt-2">{free.toLocaleString()}</p>
          {credits.data && (
            <p className="text-xs text-neutral-500 mt-1">
              {t('credits.renews_on', {
                date: fmtDate.format(new Date(credits.data.freeMonthlyResetAt)),
              })}
            </p>
          )}
        </div>
        <div className="card">
          <p className="text-xs uppercase tracking-wider text-neutral-500">
            {t('credits.issuable_total_label')}
          </p>
          <p className="text-3xl font-bold mt-2">{(balance + free).toLocaleString()}</p>
          <p className="text-xs text-neutral-500 mt-1">{t('credits.issuable_unit')}</p>
        </div>
      </div>

      <h2 className="text-xl font-semibold mb-4">{t('credits.buy_title')}</h2>
      {isEstimate && (
        <p className="text-xs text-neutral-500 mb-3">
          {t('credits.estimated_price_label', { currency: displayCurrency })}
        </p>
      )}
      {/* Layout en filas: cada tier ocupa el ancho completo del dashboard
          en lugar de competir por ~180px en una grilla de 5 columnas.
          Mobile colapsa a tarjeta vertical (todo apilado, full-width). */}
      <ul className="rounded-2xl border border-neutral-800 bg-neutral-900/30 divide-y divide-neutral-800/70 overflow-hidden mb-4">
        {visiblePackages.map((pkg) => {
          const loading = checkout.isPending && checkout.variables === pkg.id;
          const localTotal = splitCurrency(fmtLocal, pkg.totalCents / 100);
          const localPerUnit = splitCurrency(fmtLocal, pkg.perUnitCents / 100);
          const usdTotal = splitCurrency(fmtUsd, pkg.totalUsdCents / 100);
          return (
            <li
              key={pkg.id}
              className="grid gap-4 px-5 py-5 sm:px-6 sm:py-5 items-center grid-cols-1 md:grid-cols-[88px_minmax(0,1fr)_minmax(0,1.4fr)_minmax(0,1fr)_auto] md:gap-6"
            >
              <p className="text-[11px] uppercase tracking-[0.2em] text-neutral-500 md:tracking-widest">
                {pkg.id}
              </p>

              <div className="flex items-baseline gap-2">
                <span className="text-xl font-semibold tabular-nums leading-none">
                  {pkg.credits.toLocaleString(i18n.language)}
                </span>
                <span className="text-xs text-neutral-400">{t('credits.package_credits')}</span>
              </div>

              <div className="min-w-0">
                <p className="flex items-baseline gap-1.5">
                  {isEstimate && (
                    <span className="text-neutral-500 text-sm" aria-hidden>
                      ≈
                    </span>
                  )}
                  <span className="text-2xl font-semibold tabular-nums leading-none">
                    {localTotal.amount}
                  </span>
                  <span className="text-xs uppercase tracking-wider text-neutral-400 font-medium">
                    {localTotal.currency}
                  </span>
                </p>
                {isEstimate && (
                  <p className="flex items-baseline gap-1.5 mt-1.5">
                    <span className="text-neutral-600 text-xs" aria-hidden>
                      =
                    </span>
                    <span className="text-sm text-neutral-400 tabular-nums">{usdTotal.amount}</span>
                    <span className="text-[10px] uppercase tracking-wider text-neutral-500">
                      {usdTotal.currency}
                    </span>
                  </p>
                )}
              </div>

              <p className="text-sm text-neutral-400 tabular-nums">
                {localPerUnit.amount}{' '}
                <span className="text-[11px] uppercase tracking-wider text-neutral-500">
                  {localPerUnit.currency}
                </span>{' '}
                <span className="text-neutral-500">{t('credits.package_per_unit_suffix')}</span>
              </p>

              <button
                type="button"
                disabled={loading || checkout.isPending}
                onClick={() => checkout.mutate(pkg.id)}
                className="btn-primary text-sm w-full md:w-auto md:px-6 whitespace-nowrap"
              >
                {loading ? t('credits.opening_stripe') : t('credits.buy_button_short')}
              </button>
            </li>
          );
        })}
      </ul>
      <p className="text-xs text-neutral-500 mb-6 max-w-2xl">
        {t('credits.adaptive_pricing_note')}
      </p>
      {checkout.isError && (
        <p className="text-red-400 text-sm mb-6">{(checkout.error as ApiClientError).message}</p>
      )}

      <h2 className="text-xl font-semibold mb-4">{t('credits.ledger_title')}</h2>
      {credits.isLoading ? (
        <p className="text-neutral-500 text-sm">{t('credits.loading')}</p>
      ) : credits.data && credits.data.ledger.length > 0 ? (
        <div className="card overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-neutral-500 text-xs uppercase tracking-wider">
              <tr>
                <th className="text-left pb-3 pr-4">{t('credits.table_date')}</th>
                <th className="text-left pb-3 pr-4">{t('credits.table_type')}</th>
                <th className="text-right pb-3 pr-4">{t('credits.table_amount')}</th>
                <th className="text-right pb-3">{t('credits.table_balance')}</th>
              </tr>
            </thead>
            <tbody>
              {credits.data.ledger.map((row) => (
                <tr key={row.id} className="border-t border-neutral-900">
                  <td className="py-2 pr-4 text-neutral-300">
                    {fmtDate.format(new Date(row.createdAt))}
                  </td>
                  <td className="py-2 pr-4 text-neutral-300">
                    {t(`credits.ledger_type.${row.type}`)}
                  </td>
                  <td
                    className={`py-2 pr-4 text-right font-medium ${row.amount >= 0 ? 'text-emerald-400' : 'text-red-400'}`}
                  >
                    {row.amount >= 0 ? '+' : ''}
                    {row.amount.toLocaleString()}
                  </td>
                  <td className="py-2 text-right text-neutral-400">
                    {row.balanceAfter.toLocaleString()}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <p className="text-neutral-500 text-sm">{t('credits.ledger_empty')}</p>
      )}
    </DashboardShell>
  );
}

/**
 * Separa el output de `Intl.NumberFormat` con `currencyDisplay:'code'` en
 * "monto" + "código". Lo usamos para renderizar el código de moneda más
 * chico al lado del número y evitar overflow en cards angostas con cifras
 * largas (MXN/JPY/COP de planes grandes pasan los 12 chars).
 */
function splitCurrency(
  fmt: Intl.NumberFormat,
  value: number,
): { amount: string; currency: string } {
  const parts = fmt.formatToParts(value);
  let currency = '';
  const amount = parts
    .filter((p) => {
      if (p.type === 'currency') {
        currency = p.value;
        return false;
      }
      return p.type !== 'literal' || p.value.trim().length > 0;
    })
    .map((p) => p.value)
    .join('')
    .trim();
  return { amount, currency };
}
